import React, { useState, useRef, useCallback, useMemo, useEffect, useImperativeHandle, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from '@ag-grid-community/react';
import {
  ColDef,
  Column,
  ColumnPinnedEvent,
  GetMainMenuItemsParams,
  GridApi,
  IFloatingFilterParams,
  IFilterOptionDef,
  GridReadyEvent,
  MenuItemDef,
  SelectionChangedEvent,
  FilterChangedEvent,
  IDatasource,
  SortDirection,
} from '@ag-grid-community/core';
import '../../lib/agGrid';
import './AGGridTable.css';
import { buildPinKey, readPins, upsertPin, writePins, toPinMap, StoredPin, PinnedSide } from './columnPinStore';
import { buildColumnMenuItems, ActiveServerSort } from './columnMenuItems';
import {
  BombiixColDef,
  FetchRowsParams,
  FetchRowsResult,
  MoreAction,
  FloatingAction,
  AGGridTableProps,
  SearchColumnFilterHandle,
  BombiixHeaderCellProps,
  DragHandleCellParams,
  ChevronCellParams,
  MoreActionsDropdownProps,
  ActionsCellParams,
} from './AGGridTable.types';
import TruncatedText from '../TruncatedText/TruncatedText';
import { getTruncatedCellTooltip, GRID_TOOLTIP_SHOW_DELAY_MS } from '../../utils/grid-cell-tooltip';
import { getClipboardContextMenuItems } from '../../utils/grid-context-menu';

export type { AGGridTableProps, BombiixColDef, MoreAction, FloatingAction, FetchRowsParams, FetchRowsResult };

const SearchColumnFilter = forwardRef<SearchColumnFilterHandle, IFloatingFilterParams<unknown, IFilterOptionDef>>(function SearchColumnFilter(props, ref) {
  const { t } = useTranslation('samples');
  const [value, setValue] = useState('');

  useImperativeHandle(ref, () => ({
    onParentModelChanged(parentModel: { filter?: string } | null) {
      setValue(parentModel?.filter ?? '');
    },
  }));

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setValue(val);
    props.parentFilterInstance((instance) => {
      (instance as { onFloatingFilterChanged: (type: string, value: string | null) => void }).onFloatingFilterChanged('contains', val || null);
    });
  };

  return (
    <input
      type="text"
      className="ag-input-field-input ag-text-field-input ag-grid-search-input"
      placeholder={t('AG_GRID_TABLE.SEARCH_COLUMN')}
      value={value}
      onChange={onChange}
    />
  );
});

/**
 * Column header: label, active-sort indicator and a kebab that opens AG Grid's own
 * column menu. The menu itself is AG Grid's built-in component — its items are
 * declared once at grid level in `getMainMenuItems`, so nothing here renders a
 * popup. The kebab is hidden when the column sets `suppressHeaderMenuButton`.
 */
function BombiixHeaderCell({
  displayName,
  required,
  column,
  showColumnMenu,
  serverSideSort,
  activeSortColId,
  activeSortDir,
}: Readonly<BombiixHeaderCellProps>) {
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [clientSort, setClientSort] = useState<SortDirection>(column.getSort() ?? null);

  useEffect(() => {
    if (serverSideSort) return undefined;
    const onSortChanged = () => setClientSort(column.getSort() ?? null);
    column.addEventListener('sortChanged', onSortChanged);
    return () => column.removeEventListener('sortChanged', onSortChanged);
  }, [column, serverSideSort]);

  const showMenuButton = !column.getColDef().suppressHeaderMenuButton;
  const activeSort: SortDirection = serverSideSort ? (activeSortColId === column.getColId() ? activeSortDir ?? null : null) : clientSort;

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuBtnRef.current) showColumnMenu?.(menuBtnRef.current);
  };

  return (
    <div className="ag-grid-header-cell">
      <span className="ag-header-cell-text">
        <TruncatedText text={displayName} />
        {required && <span className="required-asterisk">*</span>}
      </span>

      {activeSort && <span className={`ag-grid-sort-icon ag-icon ag-icon-${activeSort === 'asc' ? 'asc' : 'desc'}`} role="presentation" />}

      {displayName && showMenuButton && (
        <button ref={menuBtnRef} type="button" className="ag-grid-col-menu-btn" onClick={onMenuClick} aria-label="Column menu" title="Column menu">
          <i className="icon-new-more" />
        </button>
      )}
    </div>
  );
}

function DragHandleCell(params: Readonly<DragHandleCellParams>) {
  const { t } = useTranslation('samples');
  const handleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (handleRef.current) params.registerRowDragger(handleRef.current);
  }, []);
  return <span ref={handleRef} className="icon-Rearrange ag-grid-drag-handle" title={t('AG_GRID_TABLE.DRAG_TO_REORDER')} />;
}

function ChevronCell(params: Readonly<ChevronCellParams>) {
  const { t } = useTranslation('samples');
  const { node } = params;
  const [, forceRender] = useState(0);
  useEffect(() => {
    const onExpanded = () => forceRender((n) => n + 1);
    node.addEventListener('expandedChanged', onExpanded);
    return () => node.removeEventListener('expandedChanged', onExpanded);
  }, [node]);

  const isExpandable = node.master || (node.group === true && (node.allChildrenCount ?? 0) > 0);
  if (!isExpandable) return null;

  const expanded = !!node.expanded;
  const toggle = () => node.setExpanded(!node.expanded);

  return (
    <button
      type="button"
      className={`ag-grid-chevron-btn${expanded ? ' expanded' : ''}`}
      onClick={toggle}
      aria-label={expanded ? t('AG_GRID_TABLE.COLLAPSE_ROW') : t('AG_GRID_TABLE.EXPAND_ROW')}
    >
      <span className="icon-new-down-arrow-icon" />
    </button>
  );
}

function MoreActionsDropdown({ actions, row, show = true }: Readonly<MoreActionsDropdownProps>) {
  const { t } = useTranslation('samples');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 160 });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && !btnRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  if (!show || !actions?.length) return null;

  return (
    <>
      <button ref={btnRef} type="button" className="btn-tbl-action" title={t('AG_GRID_TABLE.MORE_ACTIONS')} onClick={toggle}>
        <i className="icon-new-more" />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div ref={menuRef} className="ag-grid-more-dropdown" style={{ position: 'fixed', top: pos.top, left: pos.left }}>
            {actions.map((action, idx) =>
              action.divider ? (
                <div key={`divider-${idx}`} className="ag-grid-more-divider" />
              ) : (
                <button
                  key={action.label ?? `item-${idx}`}
                  type="button"
                  className={`ag-grid-more-item${action.danger ? ' danger' : ''}`}
                  onClick={() => {
                    action.onClick?.(row);
                    setOpen(false);
                  }}
                >
                  {action.icon && <i className={action.icon} />}
                  <span>{action.label}</span>
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

function ActionsCell(params: Readonly<ActionsCellParams>) {
  const { t } = useTranslation('samples');
  const { onEdit, onCopy, onDuplicate, onSave, onDelete, moreActions, getMoreActions, showMoreActions } = params;
  const {data} = params;
  const resolvedMoreActions = getMoreActions ? getMoreActions(data) : moreActions;

  return (
    <div className="ag-grid-actions-wrap">
      {onEdit && (
        <button type="button" className="btn-tbl-action" title={t('AG_GRID_TABLE.EDIT')} onClick={() => onEdit(data)}>
          <i className="icon-edit" />
        </button>
      )}
      {onCopy && (
        <button type="button" className="btn-tbl-action" title={t('AG_GRID_TABLE.COPY')} onClick={() => onCopy(data)}>
          <i className="icon-copy" />
        </button>
      )}
      {onDuplicate && (
        <button type="button" className="btn-tbl-action" title={t('AG_GRID_TABLE.DUPLICATE')} onClick={() => onDuplicate(data)}>
          <i className="icon-duplicate" />
        </button>
      )}
      {onSave && (
        <button type="button" className="btn-tbl-action btn-tbl-action--save" title={t('AG_GRID_TABLE.SAVE')} onClick={() => onSave(data)}>
          <i className="icon-Save" />
        </button>
      )}
      {onDelete && (
        <button type="button" className="btn-tbl-action btn-tbl-action--danger" title={t('AG_GRID_TABLE.DELETE')} onClick={() => onDelete(data)}>
          <i className="icon-delete" />
        </button>
      )}
      <MoreActionsDropdown actions={resolvedMoreActions ?? []} row={data} show={showMoreActions ?? false} />
    </div>
  );
}

function FloatingBottomBar({ actions }: Readonly<{ actions: FloatingAction[] }>) {
  if (!actions?.length) return null;
  return (
    <div className="ag-grid-floating-bar">
      {actions.map((action, idx) =>
        action.divider ? (
          <div key={`divider-${idx}`} className="floating-bar-divider" />
        ) : (
          <button
            key={action.label ?? `btn-${idx}`}
            type="button"
            className="floating-bar-btn"
            title={action.label}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.icon && <i className={action.icon} />}
            {action.content}
          </button>
        ),
      )}
    </div>
  );
}

function syncSelectAllCheckbox(api: GridApi, checkboxEl: HTMLInputElement | null) {
  if (!checkboxEl) return;
  let total = 0;
  api.forEachNode(() => {
    total++;
  });
  const selected = api.getSelectedRows().length;
  checkboxEl.checked = selected > 0 && selected === total;
  checkboxEl.indeterminate = selected > 0 && selected < total;
}

/** Fully-controlled AG-Grid table component. */
function AGGridTable({
  rowData,
  onFetchRows,
  pageSize = 50,
  columnDefs = [],
  selectionEnabled = true,
  showSelectAll = false,
  selectedRowIds,
  rowIdField = 'id',
  onSelectionChanged,
  onSelectAll,
  checkboxSelectionCallback = null,
  showColumnSearch = true,
  filterModel,
  onFilterChanged,
  detailCellRenderer,
  isRowMaster,
  suppressExpandColumn = false,
  onEdit,
  onCopy,
  onDuplicate,
  onSave,
  onDelete,
  moreActions,
  getMoreActions,
  showMoreActions = false,
  showDragHandle = false,
  showColumnMenuButton = true,
  floatingActions = [],
  pagination = false,
  paginationPageSize = 25,
  paginationPageSizeSelector = [10, 25, 50, 100],
  height = 'calc(100vh - 220px)',
  loading = false,
  serverSideSort = false,
  onServerSort,
  gridOptions = {},
  enableColumnPinning = false,
  persistKey,
  currentUserId,
  enableClipboardContextMenu = false,
}: AGGridTableProps) {
  const { t } = useTranslation('samples');
  const gridRef = useRef<AgGridReact>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const isServerSide = Boolean(onFetchRows);

  const pinStorageKey = enableColumnPinning && persistKey ? buildPinKey(currentUserId, persistKey) : null;
  const [savedPins, setSavedPins] = useState<StoredPin[]>(() => (pinStorageKey ? readPins(pinStorageKey) : []));

  /**
   * Persists whatever AG Grid's built-in Pin Column submenu just applied. Pinning is
   * performed by AG Grid itself; this only mirrors the resulting state to storage.
   */
  const handleColumnPinned = useCallback(
    (event: ColumnPinnedEvent) => {
      if (!pinStorageKey) return;
      const changed: Column[] = event.columns?.length ? event.columns : event.column ? [event.column] : [];
      if (!changed.length) return;
      setSavedPins((prev) => {
        const next = changed.reduce<StoredPin[]>((acc, col) => upsertPin(acc, col.getColId(), (col.getPinned() ?? null) as PinnedSide), prev);
        writePins(pinStorageKey, next);
        return next;
      });
    },
    [pinStorageKey],
  );

  const savedPinMap = useMemo(() => toPinMap(savedPins), [savedPins]);

  const [activeServerSort, setActiveServerSort] = useState<ActiveServerSort>({ colId: null, dir: null });
  /* Read inside getMainMenuItems, which AG Grid invokes on menu-open with a long-lived
     callback identity — a ref keeps the toggle reading the current sort, not a stale one. */
  const activeServerSortRef = useRef(activeServerSort);
  useEffect(() => {
    activeServerSortRef.current = activeServerSort;
  }, [activeServerSort]);

  const handleServerSortInternal = useCallback(
    (colId: string, direction: 'Asc' | 'Desc' | '') => {
      const dir: SortDirection = direction === 'Asc' ? 'asc' : direction === 'Desc' ? 'desc' : null;
      setActiveServerSort(dir ? { colId, dir } : { colId: null, dir: null });
      onServerSort?.(colId, direction);
    },
    [onServerSort],
  );

  const getMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams): (string | MenuItemDef)[] =>
      buildColumnMenuItems(params, {
        activeSort: activeServerSortRef.current,
        onServerSort: handleServerSortInternal,
        onResetColumns: () => {
          if (pinStorageKey) {
            writePins(pinStorageKey, []);
            setSavedPins([]);
          }
          params.api.resetColumnState();
        },
      }),
    [handleServerSortInternal, pinStorageKey],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (loading) api.showLoadingOverlay();
    else if (!rowData || (rowData as unknown[]).length === 0) api.showNoRowsOverlay();
    else api.hideOverlay();
  }, [loading, rowData]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || filterModel === undefined) return;
    api.setFilterModel(filterModel);
  }, [filterModel]);

  const getRowIdCallback = useCallback((params: any) => String(params?.data?.[rowIdField] ?? ''), [rowIdField]);

  const selectedRowIdsRef = useRef<string[] | undefined>(selectedRowIds);
  useEffect(() => {
    selectedRowIdsRef.current = selectedRowIds;
  }, [selectedRowIds]);

  const restoreSelection = useCallback(() => {
    const api = gridRef.current?.api;
    const ids = selectedRowIdsRef.current;
    if (!api || !ids) return;
    api.forEachNode((node) => {
      const row = node.data as Record<string, unknown> | undefined;
      const id = row?.[rowIdField];
      if (id !== undefined) {
        node.setSelected(ids.includes(String(id)), false);
      }
    });
    syncSelectAllCheckbox(api, selectAllRef.current);
  }, [rowIdField]);

  useEffect(() => {
    restoreSelection();
  }, [selectedRowIds, rowIdField, restoreSelection]);

  const datasource: IDatasource | undefined = useMemo(() => {
    if (!onFetchRows) return undefined;
    return {
      getRows: (params) => {
        const pageIndex = Math.floor(params.startRow / pageSize);
        onFetchRows({
          pageIndex,
          pageSize,
          sortModel: params.sortModel,
          filterModel: params.filterModel,
        })
          .then(({ rows, totalCount }) => {
            params.successCallback(rows, totalCount ?? -1);
          })
          .catch(() => {
            params.failCallback();
          });
      },
    };
  }, [onFetchRows, pageSize]);

  const hasActions = onEdit || onCopy || onDuplicate || onSave || onDelete || moreActions?.length || getMoreActions;

  const isRowSelectable = useMemo(() => {
    if (!checkboxSelectionCallback) return undefined;
    return (node: { data?: unknown }) => checkboxSelectionCallback({ data: node?.data, node });
  }, [checkboxSelectionCallback]);

  const noMenuProps: Partial<ColDef> = {
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    sortable: false,
    filter: false,
    floatingFilter: false,
    resizable: false,
    suppressMovable: true,
    lockPinned: true,
  };

  const builtColumnDefs = useMemo(() => {
    const cols: ColDef[] = [];

    if (showDragHandle) {
      cols.push({
        colId: '__drag',
        headerName: '',
        width: 36,
        minWidth: 36,
        maxWidth: 36,
        cellRenderer: DragHandleCell,
        pinned: 'left',
        ...noMenuProps,
      });
    }

    if (selectionEnabled) {
      cols.push({
        colId: '__checkbox',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        checkboxSelection: checkboxSelectionCallback ?? true,
        headerCheckboxSelection: !isServerSide,
        pinned: 'left',
        ...noMenuProps,
      });
    }

    if (!suppressExpandColumn) {
      cols.push({
        colId: '__expand',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        cellRenderer: ChevronCell,
        pinned: 'left',
        ...noMenuProps,
      });
    }

    columnDefs.forEach((col) => {
      const colSearchable = col.searchable !== false;
      const resolvedColId = col.colId ?? col.field;
      const hasSavedPin = enableColumnPinning && !!resolvedColId && resolvedColId in savedPinMap;
      /* Server-sorted columns are not sortable by AG Grid, so its default menu offers no
         sort items — getMainMenuItems reads this flag to inject backend-sorting ones. */
      const serverSortable = serverSideSort && col.sortable !== false;
      cols.push({
        filter: colSearchable ? 'agTextColumnFilter' : false,
        floatingFilter: colSearchable && showColumnSearch,
        floatingFilterComponent: colSearchable && showColumnSearch ? SearchColumnFilter : undefined,
        resizable: true,
        sortable: true,
        minWidth: 120,
        suppressHeaderMenuButton: !showColumnMenuButton,
        ...col,
        ...(serverSortable ? { sortable: false } : {}),
        ...(hasSavedPin ? { pinned: savedPinMap[resolvedColId as string] } : {}),
        context: { ...(col.context ?? {}), serverSortable },
        headerComponent: BombiixHeaderCell,
        headerComponentParams: {
          required: col.required ?? false,
          serverSideSort: serverSortable,
          activeSortColId: activeServerSort.colId,
          activeSortDir: activeServerSort.dir,
          ...col.headerComponentParams,
        },
      });
    });

    if (hasActions) {
      cols.push({
        colId: '__actions',
        headerName: '',
        width: 140,
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onEdit,
          onCopy,
          onDuplicate,
          onSave,
          onDelete,
          moreActions,
          getMoreActions,
          showMoreActions,
        },
        pinned: 'right',
        ...noMenuProps,
        cellClass: 'actions-cell',
      });
    }

    return cols;
  }, [
    columnDefs,
    selectionEnabled,
    checkboxSelectionCallback,
    suppressExpandColumn,
    showDragHandle,
    showColumnSearch,
    showColumnMenuButton,
    serverSideSort,
    activeServerSort,
    isServerSide,
    hasActions,
    onEdit,
    onCopy,
    onDuplicate,
    onSave,
    onDelete,
    moreActions,
    getMoreActions,
    showMoreActions,
    enableColumnPinning,
    savedPinMap,
  ]);

  const handleSelectionChanged = useCallback(
    (_e: SelectionChangedEvent) => {
      const api = gridRef.current?.api;
      if (!api) return;
      const selected = api.getSelectedRows();
      onSelectionChanged?.(selected);
      syncSelectAllCheckbox(api, selectAllRef.current);
    },
    [onSelectionChanged],
  );

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const api = gridRef.current?.api;
      if (!api) return;
      if (onSelectAll) {
        onSelectAll(e.target.checked);
      } else if (e.target.checked) {
        api.selectAll();
      } else {
        api.deselectAll();
      }
    },
    [onSelectAll],
  );

  const handleFilterChanged = useCallback(
    (_e: FilterChangedEvent) => {
      const api = gridRef.current?.api;
      if (!api) return;
      onFilterChanged?.(api.getFilterModel());
    },
    [onFilterChanged],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (isServerSide && datasource) {
        params.api.setGridOption('datasource', datasource);
      }
      if (loading) params.api.showLoadingOverlay();
    },
    [isServerSide, datasource, loading],
  );

  const defaultColDef = useMemo<ColDef>(() => ({ suppressMovable: false, tooltipValueGetter: getTruncatedCellTooltip }), []);

  return (
    <div className="ag-grid-wrapper" style={{ border: 0 }}>
      {showSelectAll && selectionEnabled && (
        <div className="ag-grid-header-controls">
          <label className="ag-grid-select-all">
            <div className="common-checkbox">
              <input type="checkbox" id="ag-grid-select-all" ref={selectAllRef} onChange={handleSelectAll} />
              <label htmlFor="ag-grid-select-all" className="chklabel">
                <span className="sr-only">{t('AG_GRID_TABLE.SELECT_ALL')}</span>
              </label>
            </div>
            <span>{t('AG_GRID_TABLE.SELECT_ALL')}</span>
          </label>
        </div>
      )}

      <div className="ag-theme-alpine ag-theme-bombiix" style={{ height, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          columnDefs={builtColumnDefs}
          rowData={isServerSide ? null : (rowData as unknown[])}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          {...(isRowSelectable ? { isRowSelectable } : {})}
          {...(!isServerSide ? { getRowId: getRowIdCallback } : {})}
          suppressRowClickSelection
          masterDetail={!!detailCellRenderer}
          detailCellRenderer={detailCellRenderer}
          isRowMaster={isRowMaster ?? (() => true)}
          animateRows={false}
          tooltipShowMode="whenTruncated"
          tooltipShowDelay={GRID_TOOLTIP_SHOW_DELAY_MS}
          suppressContextMenu={!enableClipboardContextMenu}
          {...(enableClipboardContextMenu ? { getContextMenuItems: getClipboardContextMenuItems } : {})}
          onSelectionChanged={handleSelectionChanged}
          onFilterChanged={handleFilterChanged}
          onModelUpdated={restoreSelection}
          onGridReady={onGridReady}
          suppressNoRowsOverlay={loading}
          rowModelType={isServerSide ? 'infinite' : 'clientSide'}
          {...(isServerSide ? { cacheBlockSize: pageSize, maxBlocksInCache: 10, infiniteInitialRowCount: pageSize } : {})}
          pagination={pagination}
          {...(pagination ? { paginationPageSize, paginationPageSizeSelector } : {})}
          {...gridOptions}
          getMainMenuItems={getMainMenuItems}
          {...(pinStorageKey ? { onColumnPinned: handleColumnPinned } : {})}
        />
      </div>

      <FloatingBottomBar actions={floatingActions} />
    </div>
  );
}

export default AGGridTable;
